/**
 * FinanceiroPage — FASE-C: tela honesta enquanto a cobrança não existe.
 *
 * O que havia aqui: dois KPIs (contratos ativos + soma do valor recorrente)
 * vindos de um endpoint que se declara `stub: true` (`historico: []`), embaixo
 * de um aviso "em construção". Os dois números já aparecem no Início e em
 * Contratos; o que o cliente procura numa aba chamada *Financeiro* — boleto,
 * vencimento, comprovante, segunda via — não existe em lugar nenhum ainda.
 *
 * Decisão (regra "nada de enfeite"): o item saiu do menu (ver `ClienteNav`) e a
 * rota permanece com um estado vazio honesto, pra quem tiver o link salvo. Sem
 * KPI de enfeite, sem promessa de data. Quando a integração de cobrança entrar,
 * esta tela volta a ser construída e o item retorna ao menu numa linha.
 *
 * Guard de permissão preservado: Portaria (sem `ver_financeiro`) continua sendo
 * redirecionada pro Início com aviso — mesmo comportamento de antes.
 */

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button, EmptyState } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { PortalPage } from '@/components/cliente/PortalPage';
import { useCliente } from '@/contexts/ClienteContext';
import { useClienteDashboard } from '@/lib/cliente-portal-store';

export function FinanceiroPage() {
  const { clienteAtivo } = useCliente();
  const navigate = useNavigate();
  const toast = useToast();
  const dashQuery = useClienteDashboard();
  const podeVer = dashQuery.data?.permissoes.ver_financeiro ?? false;
  const podeVerContratos = dashQuery.data?.permissoes.ver_contratos ?? false;

  useEffect(() => {
    if (dashQuery.isSuccess && !podeVer) {
      toast.show('Você não tem acesso ao financeiro.', { variant: 'info' });
      navigate('/cliente/dashboard', { replace: true });
    }
  }, [dashQuery.isSuccess, podeVer, navigate, toast]);

  if (!podeVer && dashQuery.isSuccess) return null;

  return (
    <PortalPage
      testId="financeiro-page"
      title="Financeiro"
      subtitle={
        clienteAtivo
          ? `Cobranças e pagamentos de ${clienteAtivo.nome}.`
          : 'Cobranças e pagamentos.'
      }
    >
      <div className="rounded-lg border border-line bg-paper-surface dark:border-line-dark dark:bg-paper-dark-surface">
        <EmptyState
          icon={<IconeRecibo />}
          title="Ainda não há nada para mostrar aqui"
          description="Seus dados financeiros — boletos, vencimentos e comprovantes — vão aparecer nesta tela quando a integração de cobrança estiver pronta. Enquanto isso, para segunda via ou dúvidas sobre pagamento, fale direto com a IGM."
          action={
            podeVerContratos ? (
              <Link to="/cliente/contratos">
                <Button variant="secondary">Ver meus contratos</Button>
              </Link>
            ) : undefined
          }
        />
      </div>
    </PortalPage>
  );
}

function IconeRecibo() {
  // Lucide `receipt` (SVG inline — o portal não carrega a lib de ícones).
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </svg>
  );
}
